"use client";

import * as React from "react";

import tableRows from "@hero-app/data/editor-dashboard-table.json";
import { SectionHeading } from "./section-heading";
import { SortableHeader } from "./sortable-header";
import { TableCell } from "./table-cell";
import {
  TABLE_COLUMNS,
  TABLE_SUBTITLE,
  TABLE_TITLE,
  compareTableRows,
  type TableSortState,
} from "./table-data";

export function AgentsTable() {
  const [tableSort, setTableSort] = React.useState<TableSortState | null>(null);

  const handleSortColumn = React.useCallback((columnId: string) => {
    setTableSort((prev) => {
      if (!prev || prev.columnId !== columnId) {
        return { columnId, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { columnId, direction: "desc" };
      }
      return null;
    });
  }, []);

  const sortedRows = React.useMemo(() => {
    if (!tableSort) return tableRows;
    const column = TABLE_COLUMNS.find((c) => c.id === tableSort.columnId);
    if (!column) return tableRows;
    return [...tableRows].sort((a, b) => compareTableRows(a, b, column, tableSort.direction));
  }, [tableSort]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-2 pb-2">
      <div className="flex h-full min-h-0 flex-col">
        <div className="mb-2 shrink-0">
          <SectionHeading as="h2" title={TABLE_TITLE} subtitle={TABLE_SUBTITLE} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border-[0.5px] border-[#e3e6e8] bg-[#FFFFFF80]">
          <div className="grid shrink-0 grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2 border-b-[0.5px] border-[#e3e6e8] bg-[#FFFFFF80] px-3 py-2">
            {TABLE_COLUMNS.map((column) => (
              <SortableHeader
                key={column.id}
                label={column.label}
                active={tableSort?.columnId === column.id}
                direction={tableSort?.direction ?? "asc"}
                onSort={() => handleSortColumn(column.id)}
              />
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {sortedRows.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2 border-b-[0.5px] border-[#e3e6e8]/60 px-3 py-2 last:border-b-0"
              >
                {TABLE_COLUMNS.map((column) => (
                  <TableCell key={column.id} column={column} row={row} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
